import { ai } from '../firebase/config';  // Adjust path to your config.js
import { getGenerativeModel } from 'firebase/ai';
import { getModelForFeature } from '../../services/aiConfigService';
import { logAIUsage } from '../../services/aiUsageLogger';

// Create a GenerativeModel instance - will be updated dynamically
let model = getGenerativeModel(ai, { model: 'gemini-2.5-flash' });  // Default model
let currentModelName = 'gemini-2.5-flash';

// Initialize model with configuration from Firestore
async function initializeModel(feature, schoolId) {
  const configuredModel = await getModelForFeature(feature, schoolId);
  currentModelName = configuredModel;
  model = getGenerativeModel(ai, { model: configuredModel });
  console.info(`Initialized ${feature} with model: ${configuredModel}`);
}

// Function to generate content from a prompt (Chat)
export async function generateContent(prompt, schoolId) {
  // Initialize model with current configuration for chat
  await initializeModel('chat', schoolId);

  try {
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    console.log('AI Response:', text);

    // Log AI usage for chat
    await logAIUsage('chat', currentModelName, prompt, text, schoolId);

    return text;
  } catch (error) {
    console.error('AI Error:', error);
    throw error;
  }
}

// Function to generate admission pattern configuration (uses admissionPattern model)
export async function generateAdmissionPattern(prompt, schoolId) {
  // Initialize model with admissionPattern feature configuration
  await initializeModel('admissionPattern', schoolId);

  try {
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    console.log('AI Admission Pattern Response:', text);

    // Log AI usage for admission pattern
    await logAIUsage('admissionPattern', currentModelName, prompt, text, schoolId);

    return text;
  } catch (error) {
    console.error('AI Admission Pattern Error:', error);
    throw error;
  }
}

// Function to generate auto remarks for a student
export async function generateAutoRemarks(studentName, grades, schoolId) {
  await initializeModel('autoRemarks', schoolId);

  try {
    const gradesList = grades.map(g => `- ${g.subjectName}: ${g.grade} (${g.marks}/${g.maxMarks})`).join('\n');

    const prompt = `
      Generate short, unique, and encouraging remarks (5-7 words each) for the following student's report card.
      Student Name: ${studentName}
      
      Grades:
      ${gradesList}
      
      Rules:
      1. Each remark must be strictly 5-7 words long.
      2. Remarks must be unique for each subject.
      3. STRICTLY follow these tone guidelines based on the grade:
         - A (80-100%): Exceptional, Outstanding, Excellent, Superb.
         - B (60-79%): Good, Commendable, Above Average. DO NOT use words like "Excellent", "Amazing", or "Perfect".
         - C (50-59%): Average, Satisfactory, Fair. Focus on potential for improvement.
         - D (40-49%): Below Average, Struggling. Needs clearer focus and effort.
         - E (0-39%): Weak, Failing. Critical need for intervention and hard work.
      4. Ensure the tone matches the grade precisely. Do not praise a B like an A.
      5. Output MUST be valid JSON only, with subject names as keys and remarks as values.
      
      Example Output:
      {
        "Mathematics": "Excellent mastery of concepts shown here.",
        "Science": "Good understanding of basics, keep improving."
      }
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    let text = response.text();

    // Clean up markdown code blocks if present
    text = text.replace(/```json/g, '').replace(/```/g, '').trim();

    console.log('AI Auto Remarks Response:', text);

    // Log usage
    await logAIUsage('autoRemarks', currentModelName, prompt, text, schoolId);

    return JSON.parse(text);
  } catch (error) {
    console.error('AI Auto Remarks Error:', error);
    throw error;
  }
}

// Function to generate navigation suggestions
export async function generateNavigationSuggestion(query, schoolId, role = 'admin') {
  await initializeModel('navigationSearch', schoolId);

  const adminRoutes = `
    - /admin-dashboard: Main dashboard overview
    - /dashboard/registerschool: Register a new school
    - /dashboard/editschool: Edit school details
    - /classes: Manage classes and streams (create class, view classes)
    - /dashboard/academics: Manage academic years, grading, subjects (Grade Entry, Academics)
    - /lessons: Subjects Management (assign subjects, view subjects)
    - /library: Library management (borrow, return, books, members)
    - /students: Student Management (add student, view students, attendance)
    - /research: AI Research Tool
    - /explore: Resource Dashboard
    - /attendance: Admin Attendance Analytics
    - /dashboard/Staff: Staff Directory (manage teachers, add new staff, view profiles)
    - /dashboard/communication: Communication Center (SMS, Emails, Announcements, Newsletters)
    - /dorms: Dormitory Management (assign rooms, manage capacity, housing)
    - /finance-dashboard: Financial Controller (Fee structure, payments, expenses, reports)
    - /dashboard/subscription: System Subscription & Billing
    - /chatbot: AI Assistant Configuration
    - /lab-management: Science Labs & Inventory (manage equipment, bookings)
    - /duty-roster: Staff Duty Roster (assign duties, view schedule)
    - /exam-timetable: Examination Scheduler (create exam dates, invigilation)
    - /dashboard/settings: System Settings (School config, roles, permissions)
  `;

  const teacherRoutes = `
    - /faculty-dashboard: Main Dashboard
    - /dashboard/timetable: View Class Timetable
    - /dashboard/grading: Enter and Manage Grades
    - /dashboard/dutyroster: View Duty Roster
    - /dashboard/labs: View Lab Schedules and Bookings
    - /dashboard/exam-timetable: View Exam Timetable
    - /dashboard/school-calendar: School Calendar
    - /dashboard/library: Library Management for Teachers
    - /dashboard/explore: Educational Resources
    - /dashboard/research: AI Research Assistant
    - /groups-management: Manage Student Groups
    - /dashboard/academics: Academic Overview (academic years, terms, basic setup)
    - /dashboard/communication: Communication Hub (send messages to parents, email staff, announcements)
    - /dashboard/attendance: Student Attendance (take roll call, mark present/absent, view history)
    - /dashboard/settings: Teacher Profile & Settings (change password, update personal info)
  `;

  const guardianRoutes = `
    - /guardian-dashboard: Parent Dashboard (Child's overview, progress validation)
    - /dashboard/finance: Financial Portal (Pay school fees, view invoices, check balances, history)
    - /dashboard/school-calendar: School Events & Holidays
    - /dashboard/communication: Message Center (Contact teachers, school announcements)
    - /dashboard/settings: Account Settings (Profile, contact details)
  `;

  const studentRoutes = `
    - /student-dashboard: Main Student Dashboard (Overview, Gamification, Stats, Progress)
    - /dashboard/school-calendar: School Events Calendar (holidays, exams, important dates)
    - /dashboard/attendance: My Attendance Record (check days present/absent)
    - /dashboard/class-schedule: My Class Timetable (daily lessons, schedule)
    - /dashboard/communication: Messages & Notices (inbox, contact teachers)
    - /dashboard/settings: My Profile Settings (update info, preferences)
  `;

  let availableRoutes = "";
  if (role === 'school_staff' || role === 'teacher') availableRoutes = teacherRoutes;
  else if (role === 'guardian') availableRoutes = guardianRoutes;
  else if (role === 'student') availableRoutes = studentRoutes;
  else availableRoutes = adminRoutes;

  try {
    const prompt = `
      You are an intelligent navigation assistant.
      The user is a ${role}. You must return the most appropriate route from the list below ONLY.
      Do NOT hallucinate or suggest routes not in this list.
      
      Available Routes for ${role}:
      ${availableRoutes}

      User Query: "${query}"

      Instructions:
      1. Analyze the user's intent.
      2. Match it to the most relevant route from the provided list.
      3. If no route is relevant, return null for the route.
      4. Output MUST be valid JSON only.
      
      Format:
      {
        "route": "/path/to/route" or null,
        "label": "Name of the page",
        "reason": "Brief explanation of why this route was chosen"
      }
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    let text = response.text();

    // Clean up markdown code blocks if present
    text = text.replace(/```json/g, '').replace(/```/g, '').trim();

    console.log(`AI Navigation Response (${role}):`, text);

    // Log usage
    await logAIUsage('navigationSearch', currentModelName, query, text, schoolId);

    return JSON.parse(text);
  } catch (error) {
    console.error('AI Navigation Error:', error);
    throw error;
  }
}